import React, { useState, useRef, useEffect } from 'react';
import { useProperty } from '../../context/PropertyContext';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../Auth/AuthModal';
import ChangePasswordModal from '../Auth/ChangePasswordModal';

// No hardcoded fallback here on purpose — a real email would otherwise ship in
// the JS bundle. If the env var is unset, the admin nav item just stays
// hidden (this is a UI convenience only; real enforcement is server-side).
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || '';
import './Navbar.css';

const NAV_ITEMS = [
  {
    id: 'finder', label: 'Find Rooms',
    icon: (active) => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
        <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
      </svg>
    )
  },
  {
    id: 'list', label: 'List',
    icon: (active) => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
    )
  },
  {
    id: 'favorites', label: 'Saved',
    icon: (active) => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    )
  },
  {
    id: 'alerts', label: 'Alerts',
    icon: (active) => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    )
  },
  {
    id: 'blog', label: 'Blog',
    icon: (active) => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
      </svg>
    )
  },
  {
    id: 'support', label: 'Support',
    icon: (active) => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 12h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 14"/>
        <path d="m7 18 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"/>
        <path d="m2 13 6 6"/>
      </svg>
    )
  },
];

export default function Navbar() {
  const { activeTab, setActiveTab, goHome } = useProperty();
  const { user, logout, favorites, updateAvatar } = useAuth();
  // UI only — every admin API call is re-checked on the server.
  const isAdmin = !!user && (user.isAdmin === true || (!!ADMIN_EMAIL && user.email === ADMIN_EMAIL));
  const [showAuth, setShowAuth] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const avatarInputRef = useRef();
  const menuRef = useRef();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    setAvatarError('');
    try {
      await updateAvatar(file);
    } catch (err) {
      setAvatarError(err.response?.data?.message || 'Upload failed. Try a smaller photo.');
      setTimeout(() => setAvatarError(''), 4000);
    } finally {
      setAvatarUploading(false); e.target.value = '';
    }
  };

  // Reusable avatar display: image if available, else letter initial
  const AvatarImg = ({ size = 32, className = 'user-avatar' }) => (
    user?.avatar
      ? <img src={user.avatar} alt={user.name} className={className} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
      : <div className={className} style={size !== 32 ? { width: size, height: size, fontSize: size * 0.4 } : {}}>{user?.name?.[0]?.toUpperCase() || '?'}</div>
  );

  const handleShareApp = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Spaczo – Find Rooms & Flats | Zero Brokerage',
          text: 'Find rooms, flats & PGs across India with zero brokerage. Direct owner contact!',
          url: 'https://www.spaczo.com',
        });
      } else {
        await navigator.clipboard.writeText('https://www.spaczo.com');
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      }
    } catch {}
  };

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, []);

  // Desktop: all tabs including Agreement + Support
  const desktopNavItems = user
    ? [...NAV_ITEMS, {
        id: 'mylistings', label: 'Mine',
        icon: (active) => (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
        )
      }]
    : NAV_ITEMS;

  // Mobile bottom nav: only 4 core tabs (no Agreement/Support — those go in menu)
  const MOBILE_NAV_IDS = ['finder', 'list', 'favorites', 'alerts'];
  const mobileNavItems = NAV_ITEMS.filter(i => MOBILE_NAV_IDS.includes(i.id));

  return (
    <>
      {/* ── Top Navbar ── */}
      <nav className="navbar">
        <button className="navbar-brand" onClick={goHome}>
          <div className="brand-icon-wrap">📍</div>
          <span className="brand-name"><span className="brand-text">Spacz</span><span className="brand-o">O</span></span>
        </button>

        {/* Desktop tabs — hidden on mobile */}
        <div className="navbar-tabs desktop-only">
          {desktopNavItems.map(item => (
            <button
              key={item.id}
              className={`tab-btn ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => item.id === 'finder' ? goHome() : setActiveTab(item.id)}
            >
              <span className="tab-btn-icon">{item.icon(activeTab === item.id)}</span>
              {item.label}
              {item.id === 'favorites' && favorites.length > 0 && (
                <span className="fav-count">{favorites.length}</span>
              )}
            </button>
          ))}
        </div>

        <div className="navbar-right">
          {user ? (
            <div className="user-menu-wrap desktop-only" ref={menuRef}>
              <button className="user-avatar-btn" onClick={() => setShowUserMenu(p => !p)}>
                <AvatarImg size={32} />
                <span className="user-name">{user.name?.split(' ')[0] || 'Account'}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {showUserMenu && (
                <div className="user-dropdown" onPointerDown={(e) => e.stopPropagation()}>
                  <div className="user-dropdown-header">
                    <div className="avatar-upload-wrap" onClick={() => avatarInputRef.current?.click()} title="Change photo">
                      <AvatarImg size={44} className="user-avatar large" />
                      <div className="avatar-upload-overlay">{avatarUploading ? '…' : '📷'}</div>
                    </div>
                    <div>
                      <div className="dropdown-name">{user.name || 'Account'}</div>
                      <div className="dropdown-email">{user.email}</div>
                      <div className={`avatar-upload-hint${avatarError ? ' avatar-upload-error' : ''}`}>{avatarError || 'Tap photo to change'}</div>
                    </div>
                  </div>
                  <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
                  <hr />
                  <button onClick={() => { setActiveTab('mylistings'); setShowUserMenu(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                    My Listings
                  </button>
                  <button onClick={() => { setActiveTab('favorites'); setShowUserMenu(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    Saved ({favorites.length})
                  </button>
                  <button onClick={() => { setActiveTab('blog'); setShowUserMenu(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                    Blog
                  </button>
                  <button onClick={() => { setActiveTab('verification'); setShowUserMenu(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 12l2 2 4-4"/><path d="M21 12c0 4.97-4.03 9-9 9S3 16.97 3 12 7.03 3 12 3s9 4.03 9 9z"/></svg>
                    Get Verified
                  </button>
                  {isAdmin && (
                    <button style={{ color: '#f59e0b', fontWeight: 800 }} onClick={() => { setActiveTab('admin'); setShowUserMenu(false); }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                      Admin Panel
                    </button>
                  )}
                  <button onClick={() => { setShowUserMenu(false); setShowChangePassword(true); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    Change Password
                  </button>
                  <button className="share-btn" onClick={handleShareApp}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                    {shareCopied ? 'Link copied!' : 'Share Spaczo'}
                  </button>
                  <hr />
                  <button className="logout-btn" onMouseDown={() => logout()}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="signin-btn" onClick={() => setShowAuth(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              Sign In
            </button>
          )}
        </div>
      </nav>

      {/* ── Bottom Nav — mobile only ── */}
      <nav className="bottom-nav mobile-only">
        {mobileNavItems.map(item => (
          <button
            key={item.id}
            className={`bnav-item ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => item.id === 'finder' ? goHome() : setActiveTab(item.id)}
          >
            <span className="bnav-icon">
              {item.icon(activeTab === item.id)}
              {item.id === 'favorites' && favorites.length > 0 && (
                <span className="bnav-badge">{favorites.length}</span>
              )}
            </span>
            <span className="bnav-label">{item.label}</span>
          </button>
        ))}
        {/* Profile button in bottom nav */}
        {user ? (
          <button
            className={`bnav-item ${showUserMenu ? 'active' : ''}`}
            onClick={() => setShowUserMenu(p => !p)}
            ref={menuRef}
          >
            <span className="bnav-icon">
              {user?.avatar
                ? <img src={user.avatar} alt={user.name} className="bnav-avatar" style={{ borderRadius: '50%', objectFit: 'cover' }} />
                : <div className="bnav-avatar">{user.name?.[0]?.toUpperCase()}</div>}
            </span>
            <span className="bnav-label">{user.name?.split(' ')[0] || 'Me'}</span>
          </button>
        ) : (
          <>
            {/* Logged out: a small menu so Blog / Support / Agreement are
                reachable on phones too, not only after signing in */}
            <button
              className={`bnav-item ${showUserMenu ? 'active' : ''}`}
              onClick={() => setShowUserMenu(p => !p)}
              ref={menuRef}
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
            >
              <span className="bnav-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              <span className="bnav-label">Account</span>
            </button>
          </>
        )}

        {showUserMenu && !user && (
          <div className="bnav-user-popup" role="menu" onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
            <button className="bnav-guest-signin" onClick={() => { setShowUserMenu(false); setShowAuth(true); }}>
              Sign in / Create account
            </button>
            <hr />
            <button onClick={() => { setActiveTab('blog'); setShowUserMenu(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
              Blog
            </button>
            <button onClick={() => { setActiveTab('agreement'); setShowUserMenu(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Free Rent Agreement
            </button>
            <button onClick={() => { setActiveTab('support'); setShowUserMenu(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Support
            </button>
            <button className="share-btn" onClick={handleShareApp}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              {shareCopied ? 'Link copied!' : 'Share Spaczo'}
            </button>
          </div>
        )}

        {/* Mobile user dropdown */}
        {showUserMenu && user && (
          <div className="bnav-user-popup" onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
            <div className="bnav-popup-user">
              <div className="avatar-upload-wrap" onClick={() => avatarInputRef.current?.click()} title="Change photo">
                <AvatarImg size={44} className="user-avatar large" />
                <div className="avatar-upload-overlay">{avatarUploading ? '…' : '📷'}</div>
              </div>
              <div>
                <div className="dropdown-name">{user.name || 'Account'}</div>
                <div className="dropdown-email">{user.email}</div>
                <div className={`avatar-upload-hint${avatarError ? ' avatar-upload-error' : ''}`}>{avatarError || 'Tap photo to change'}</div>
              </div>
            </div>
            <hr />
            <button onClick={() => { setActiveTab('mylistings'); setShowUserMenu(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
              My Listings
            </button>
            <button onClick={() => { setActiveTab('favorites'); setShowUserMenu(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              Saved ({favorites.length})
            </button>
            <button onClick={() => { setActiveTab('blog'); setShowUserMenu(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
              Blog
            </button>
            <button onClick={() => { setActiveTab('verification'); setShowUserMenu(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 12l2 2 4-4"/><path d="M21 12c0 4.97-4.03 9-9 9S3 16.97 3 12 7.03 3 12 3s9 4.03 9 9z"/></svg>
              Get Verified
            </button>
            <button onClick={() => { setActiveTab('support'); setShowUserMenu(false); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              Support
            </button>
            {isAdmin && (
              <button style={{ color: '#f59e0b', fontWeight: 800 }} onClick={() => { setActiveTab('admin'); setShowUserMenu(false); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                Admin Panel
              </button>
            )}
            <button onClick={() => { setShowUserMenu(false); setShowChangePassword(true); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Change Password
            </button>
            <button className="share-btn" onClick={handleShareApp}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              {shareCopied ? 'Link copied!' : 'Share Spaczo'}
            </button>
            <hr />
            <button className="logout-btn" onMouseDown={() => logout()}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              Logout
            </button>
          </div>
        )}
      </nav>

      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  );
}
